import { FC, useEffect } from 'react';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';
import Carousel from 'react-multi-carousel';
import { Element, scroller } from 'react-scroll';
import { useWindowSize } from 'react-use';

import CertificateBundleCard from './certificateBundleCard';

import ArrowCircleButton from '@components/atoms/arrowCircleButton';
import { useCourseCertificateBundles } from '@hooks/course/courseDetail/useCourseCertificateBundle';

import 'react-multi-carousel/lib/styles.css';
import ProductCriteriaCertificateBundleStyle from './style';

type ProductCriteriaCertificateBundleProps = {
  scrollElementName: string;
  courseId: number;
};

const ProductCriteriaCertificateBundle: FC<ProductCriteriaCertificateBundleProps> = ({
  scrollElementName,
  courseId,
}) => {
  const { t } = useTranslation('store_course');
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { width } = useWindowSize();
  const CARD_WIDTH = 1080;
  const { handleFetchCourseCertificateBundles, courseCertificateBundlesData } = useCourseCertificateBundles();
  useEffect(() => {
    if (inView) {
      handleFetchCourseCertificateBundles(courseId);
    }
  }, [inView]);

  // Scroll to the section programmatically after the page loads (if needed)
  useEffect(() => {
    const scrollToSection = () => {
      const hash = window.location.hash.substring(1); // Get fragment identifier without '#'
      if (hash && hash === scrollElementName) {
        scroller.scrollTo(hash, {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          offset: -120,
        });
      }
    };

    scrollToSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardCount = Math.max(1, Math.floor(width! / CARD_WIDTH));

  const responsive = {
    largeDesktop: {
      breakpoint: { max: 6000, min: 2000 },
      items: 1,
      slidesToSlide: 1,
    },
    mediumDesktop: {
      breakpoint: { max: 1999, min: 1701 },
      items: 1,
      slidesToSlide: 1,
    },
    desktop: {
      breakpoint: { max: 1700, min: 1200 },
      items: 1,
      slidesToSlide: 1,
    },
    tablet: {
      breakpoint: { max: 1199, min: 768 },
      items: 1,
      slidesToSlide: 1,
    },
    mobile: {
      breakpoint: { max: 767, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };

  const CustomLeftArrow = ({ onClick }: any) => (
    <Box className="previous-button" onClick={onClick}>
      <KeyboardArrowLeftIcon fontSize="large" />
    </Box>
  );
  const CustomRightArrow = ({ onClick }: any) => (
    <Box className="next-button" onClick={onClick}>
      <KeyboardArrowRightIcon fontSize="large" />
    </Box>
  );

  return (
    <Element name={scrollElementName}>
      <ProductCriteriaCertificateBundleStyle>
        <Box className="custom-background" ref={ref} py={{ xs: 6, md: 10 }}>
          {inView && courseCertificateBundlesData && courseCertificateBundlesData.length > 0 ? (
            <Carousel
              responsive={responsive}
              partialVisible
              draggable={width! < 1200}
              swipeable={width! < 1200}
              containerClass="carousel-container"
              itemClass={`carousel-item ${courseCertificateBundlesData.length <= cardCount && 'width'}`}
              ssr
              deviceType="desktop"
              customRightArrow={<CustomRightArrow />}
              customLeftArrow={<CustomLeftArrow />}
              removeArrowOnDeviceType={[]}
            >
              {courseCertificateBundlesData.map((bundle) => (
                <CertificateBundleCard key={bundle.id} bundle={bundle} t={t} />
              ))}
            </Carousel>
          ) : null}
        </Box>
      </ProductCriteriaCertificateBundleStyle>
    </Element>
  );
};

export default ProductCriteriaCertificateBundle;
